import {useState, useEffect, useRef} from 'react'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {isAGECProduct} from '../utils/product-utils'
import {AGEC_PRODUCT_ATTRIBUTES} from '../constants'

/**
 * Custom hook to handle product variant logic based on specific conditions.
 *
 * @param {Object} product - The product object containing variant details.
 * @param {boolean} isAGEC - Flag indicating if the product is part of the AGEC (Anti-Gaspillage et Économie Circulaire) initiative.
 * @param {boolean} shouldFetch - Determines whether additional data fetching is required for the product variant.
 * @returns {Object} - Returns an object containing the selected product variant and any related data.
 */
const useProductVariant = (product, isAGEC, shouldFetch = true) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const [variant, setVariant] = useState(null)
    const fetchedProductsRef = useRef({})

    useEffect(() => {
        const fetchProduct = async (id, isAGEC) => {
            if (fetchedProductsRef.current[id]) {
                return fetchedProductsRef.current[id]
            }

            const token = await getTokenWhenReady()
            const parameters = {id: id}

            if (isAGEC) {
                parameters.select = `(${AGEC_PRODUCT_ATTRIBUTES.join(',')})`
            }

            const variantForProduct = await api.shopperProducts.getProduct({
                parameters: parameters,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            fetchedProductsRef.current[id] = variantForProduct

            return variantForProduct
        }

        /**
         * Asynchronously fetches and sets the product variant based on the given product object.
         * If the product is a variant type, it sets the variant directly. Otherwise, it attempts
         * to fetch the first variant of the product and sets it as the variant.
         *
         * @async
         * @function fetchVariant
         * @returns {void} This function does not return a value.
         */
        const fetchVariant = async () => {
            if (!product) {
                return
            }
            if (product?.type?.variant) {
                setVariant(product)
            } else {
                const representingProduct = getFirstVariant(product)
                if (representingProduct?.productId) {
                    const fetchedProduct = await fetchProduct(representingProduct.productId, isAGEC)
                    setVariant(fetchedProduct)
                } else {
                    setVariant(null)
                }
            }
        }

        if (product && shouldFetch) {
            fetchVariant()
        }
    }, [product?.productId, product?.id, shouldFetch])

    return variant
}

/**
 * Retrieves the first applicable product variant based on color and size variations.
 *
 * This function determines the first variant of a product by checking its color and size
 * attributes. It first identifies the color variation based on the product type, whether
 * it's a master product or a variation group. Then, it finds the first size variation.
 * Finally, it searches for a variant that matches both the identified color and size.
 *
 * @param {Object} product - The product object containing variation attributes and variants.
 * @param {string} product.type - The type of the product, which can be 'master' or 'variationGroup'.
 * @param {Array} product.variationAttributes - An array of variation attributes such as color and size.
 * @param {Array} product.variants - An array of variant objects, each containing variation values.
 * @returns {Object|null} - The first matching product variant object or null if no match is found.
 */
const getFirstVariant = (product) => {
    if (!product) {
        return null
    }

    let colorVariation
    if (product?.type?.master) {
        colorVariation = getFirstVariation(product.variationAttributes, 'color')
    } else if (product?.type?.variationGroup) {
        colorVariation = product.c_color
    }

    if (!colorVariation) {
        return null
    }

    const firstSizeID = getFirstVariation(product.variationAttributes, 'size')
    if (!firstSizeID) {
        return null
    }

    const correspondingVariant = product.variants?.find(
        (variant) =>
            variant.variationValues?.size === firstSizeID &&
            variant.variationValues?.color === colorVariation
    )

    return correspondingVariant || null
}

/**
 * Retrieves the first variation value for a specified type from a list of variation attributes.
 *
 * @param {Array<Object>} variationAttributes - An array of variation attribute objects, each containing an id and values.
 * @param {string} type - The type of variation to search for within the variation attributes.
 * @returns {string|null} The value of the first variation found for the specified type, or null if no matching variation is found.
 */
function getFirstVariation(variationAttributes, type) {
    if (!variationAttributes) {
        return null
    }

    const variation = variationAttributes.find(({id}) => id === type)
    return variation?.values?.[0]?.value || null
}

/**
 * Custom hook to retrieve a product variant based on AGEC product criteria.
 *
 * @param {Object} product - The product object for which the variant is to be retrieved.
 * @param {boolean} [shouldFetch=true] - Optional flag indicating whether to fetch the product variant.
 * @returns {Object|null} - Returns the product variant if the product is an AGEC product; otherwise, returns null.
 */
export const useAGECProduct = (product, shouldFetch = true) => {
    const variant = useProductVariant(product, true, shouldFetch)
    return isAGECProduct(product) ? variant : null
}

export default useProductVariant
